import { useEffect, useRef, useState } from 'react';
import type { SiteEntity } from '../../../types/site';
import type { ProjectIntent } from '../../../types/siteRag';
import type { GenerationProjectInputChanges, GenerationProjectInputEditScope, SyncGenerationProjectInputsResult } from '../../../types/generationProjectInputSync';
import { callUserFunction } from '../../../lib/callUserFunction';
import { generationProjectInputHash, applyGenerationProjectInputChanges, activeProjectInputBindingBeforeEdit } from '../../../lib/generationImpact/generationProjectInputSync';
import { intentDecisionStateHash } from '../../../lib/intent/undecidedProjectIntent';
import { resolveSiteStudyArea } from '../../../lib/siteStudyArea';
import { hashGenerationImpactValue } from '../../../lib/generationImpact/integrity';
import { useStore } from '../../../store';

interface Options {
  site: SiteEntity;
  editingAssessment: GenerationProjectInputEditScope;
  enabled: boolean;
  onSaved?: () => Promise<void>;
}

/** Runs only from an actual field blur or explicit user action. Mount,
 * hydration, inferred defaults and background scenario saves never call it. */
export function useGenerationProjectInputSync({ site, editingAssessment, enabled, onSaved }: Options) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const mounted = useRef(true);
  const pending = useRef(false);
  const focus = useRef<{ site: SiteEntity; decisionHash: string; editingAssessment: GenerationProjectInputEditScope } | null>(null);
  const current = useRef({ site, editingAssessment, enabled, onSaved });
  current.current = { site, editingAssessment, enabled, onSaved };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const captureEdit = () => {
    const value = current.current;
    focus.current = value.enabled ? { site: value.site, decisionHash: intentDecisionStateHash(value.site), editingAssessment: value.editingAssessment } : null;
  };
  const save = async (changes: GenerationProjectInputChanges): Promise<void> => {
    if (!current.current.enabled || pending.current) return;
    const subject = focus.current ?? { site: current.current.site,
      decisionHash: intentDecisionStateHash(current.current.site), editingAssessment: current.current.editingAssessment };
    focus.current = null;
    // A reset/selection change during the actual edit is a conflict, not a new
    // baseline to which the stale input may be silently applied.
    if (subject.site.id !== current.current.site.id || subject.decisionHash !== intentDecisionStateHash(current.current.site)) {
      setMessage('Project inputs changed while editing. Review the current values before saving.'); return;
    }
    let expectedIntent: ProjectIntent;
    try { expectedIntent = applyGenerationProjectInputChanges(subject.site.projectIntent, changes); }
    catch (error) { setMessage((error as Error).message); return; }
    if (generationProjectInputHash(expectedIntent) === generationProjectInputHash(subject.site.projectIntent)) return;
    pending.current = true; setBusy(true); setMessage('');
    try {
      const ownerUid = subject.site._ownerUid ?? subject.site.createdBy?.uid;
      if (!ownerUid) throw new Error('Canonical site owner is unavailable.');
      const result = await callUserFunction<SyncGenerationProjectInputsResult>('syncGenerationProjectInputs', {
        siteId: subject.site.id, ownerUid, expectedDecisionHash: subject.decisionHash,
        expectedStudyAreaSelectionHash: resolveSiteStudyArea(subject.site)?.scope.selectionHash ?? null,
        editingAssessment: subject.editingAssessment, changes,
      });
      const expectedDecisionHash = intentDecisionStateHash({ ...subject.site, projectIntent: expectedIntent });
      const expectedBinding = activeProjectInputBindingBeforeEdit(subject.site);
      if (result.siteId !== subject.site.id || result.ownerUid !== ownerUid
        || result.sharedInputHash !== generationProjectInputHash(expectedIntent)
        || hashGenerationImpactValue(result.projectIntent) !== hashGenerationImpactValue(expectedIntent)
        || generationProjectInputHash(result.projectIntent) !== result.sharedInputHash
        || hashGenerationImpactValue(result.generationProjectInputBinding ?? null) !== hashGenerationImpactValue(expectedBinding ?? null)
        || result.decisionHash !== expectedDecisionHash) {
        throw new Error('The saved shared-input response could not be verified. Refresh the site.');
      }
      // Cache adoption is exact CAS of the trusted server response, not a
      // second client Firestore write or a merge into a newer/reset Intent.
      useStore.setState(state => ({ siteRag: { ...state.siteRag,
        sites: state.siteRag.sites.map(candidate => candidate.id !== subject.site.id
          || intentDecisionStateHash(candidate) !== subject.decisionHash ? candidate
          : { ...candidate, projectIntent: result.projectIntent,
            ...(result.generationProjectInputBinding ? { generationProjectInputBinding: result.generationProjectInputBinding } : {}),
            ...(result.updatedAt ? { siteConfigUpdatedAt: result.updatedAt, updatedAt: result.updatedAt, lastDataChangeAt: result.updatedAt } : {}) }),
      } }));
      if (result.changed) {
        useStore.getState()._staleStampSiteComposite(subject.site.id);
        void useStore.getState().recomputeEvidenceReadiness(subject.site.id);
      }
      if (mounted.current && current.current.site.id === subject.site.id) await current.current.onSaved?.();
      if (mounted.current && current.current.site.id === subject.site.id) setMessage('Shared project inputs saved to Intent. Affected saved assessments require a new calculation and selection.');
    } catch (error) {
      if (mounted.current && current.current.site.id === subject.site.id) setMessage((error as Error).message);
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return { captureEdit, save, busy, message };
}
